// Engine cờ vây (Go/Weiqi) bàn 9x9: luật bắt quân, tự sát, Ko đơn giản, tính điểm theo
// diện tích (Chinese rules) + AI đánh theo heuristic (ăn quân > tránh tự đưa vào atari > áp lực > thế).
export const SIZE = 9;
export const EMPTY = null;
export const BLACK = 'B'; // người chơi, đi trước
export const WHITE = 'W'; // máy, được cộng komi vì đi sau
export const KOMI = 6.5;

function key(x, y) {
  return y * SIZE + x;
}

function neighbors(x, y) {
  const list = [];
  if (x > 0) list.push([x - 1, y]);
  if (x < SIZE - 1) list.push([x + 1, y]);
  if (y > 0) list.push([x, y - 1]);
  if (y < SIZE - 1) list.push([x, y + 1]);
  return list;
}

export function createEmptyBoard() {
  return Array.from({ length: SIZE }, () => Array(SIZE).fill(EMPTY));
}

export function cloneBoard(board) {
  return board.map((row) => row.slice());
}

// Loang (flood fill) toàn bộ nhóm quân cùng màu nối với (x,y) + tập hợp khí (ô trống liền kề).
function getGroup(board, x, y) {
  const color = board[y][x];
  if (!color) return null;
  const stones = [[x, y]];
  const visited = new Set([key(x, y)]);
  const liberties = new Set();
  let i = 0;
  while (i < stones.length) {
    const [cx, cy] = stones[i++];
    for (const [nx, ny] of neighbors(cx, cy)) {
      const nk = key(nx, ny);
      const val = board[ny][nx];
      if (val === EMPTY) liberties.add(nk);
      else if (val === color && !visited.has(nk)) {
        visited.add(nk);
        stones.push([nx, ny]);
      }
    }
  }
  return { color, stones, liberties };
}

// Đánh quân `color` vào (x,y): bắt quân đối phương hết khí, cấm nước tự sát.
// Trả về { legal:false } nếu ô đã có quân hoặc là nước tự sát; ngược lại { legal:true, board, captured }.
export function playMove(board, x, y, color) {
  if (board[y][x] !== EMPTY) return { legal: false };
  const opponent = color === BLACK ? WHITE : BLACK;
  const next = cloneBoard(board);
  next[y][x] = color;

  const captured = [];
  const seenGroups = new Set();
  for (const [nx, ny] of neighbors(x, y)) {
    if (next[ny][nx] !== opponent) continue;
    const gk = key(nx, ny);
    if (seenGroups.has(gk)) continue;
    const group = getGroup(next, nx, ny);
    group.stones.forEach(([sx, sy]) => seenGroups.add(key(sx, sy)));
    if (group.liberties.size === 0) {
      group.stones.forEach(([sx, sy]) => {
        next[sy][sx] = EMPTY;
      });
      captured.push(...group.stones);
    }
  }

  const ownGroup = getGroup(next, x, y);
  if (ownGroup.liberties.size === 0) return { legal: false }; // tự sát, không hợp lệ

  return { legal: true, board: next, captured };
}

// Luật Ko rút gọn: nếu nước vừa đánh bắt đúng 1 quân và quân mình vừa đặt là 1 quân lẻ chỉ
// còn 1 khí (hình Ko kinh điển), cấm đối phương đánh ngay lại vào đúng ô vừa bị bắt.
export function computeKoPoint(resultBoard, x, y, captured) {
  if (captured.length !== 1) return null;
  const group = getGroup(resultBoard, x, y);
  if (group.stones.length === 1 && group.liberties.size === 1) {
    return { x: captured[0][0], y: captured[0][1] };
  }
  return null;
}

// Tính điểm cuối ván theo diện tích: quân trên bàn + đất bao quanh (vùng trống chỉ giáp 1 màu).
export function scoreBoard(board, komi = KOMI) {
  let blackStones = 0;
  let whiteStones = 0;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (board[y][x] === BLACK) blackStones++;
      else if (board[y][x] === WHITE) whiteStones++;
    }
  }

  let blackTerritory = 0;
  let whiteTerritory = 0;
  const visited = new Set();
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (board[y][x] !== EMPTY || visited.has(key(x, y))) continue;
      const region = [[x, y]];
      visited.add(key(x, y));
      const borders = new Set();
      let i = 0;
      while (i < region.length) {
        const [cx, cy] = region[i++];
        for (const [nx, ny] of neighbors(cx, cy)) {
          const nk = key(nx, ny);
          const val = board[ny][nx];
          if (val === EMPTY) {
            if (!visited.has(nk)) {
              visited.add(nk);
              region.push([nx, ny]);
            }
          } else {
            borders.add(val);
          }
        }
      }
      if (borders.size === 1) {
        const owner = [...borders][0];
        if (owner === BLACK) blackTerritory += region.length;
        else whiteTerritory += region.length;
      }
    }
  }

  const blackScore = blackStones + blackTerritory;
  const whiteScore = whiteStones + whiteTerritory + komi;
  return {
    blackStones,
    whiteStones,
    blackTerritory,
    whiteTerritory,
    blackScore,
    whiteScore,
    komi,
    winner: blackScore > whiteScore ? BLACK : WHITE,
    diff: Math.round(Math.abs(blackScore - whiteScore) * 10) / 10,
  };
}

function evaluateMove(board, x, y, color, opponent) {
  const result = playMove(board, x, y, color);
  if (!result.legal) return null;

  const capturedCount = result.captured.length;
  const ownGroup = getGroup(result.board, x, y);
  const ownLiberties = ownGroup.liberties.size;

  let score = capturedCount * 1000;
  if (capturedCount === 0 && ownLiberties === 1) score -= 400; // tự đưa vào atari, trừ trừ khi ăn được bù lại

  // áp lực: sau nước này đối phương có nhóm nào rơi vào atari (còn 1 khí) không
  for (const [nx, ny] of neighbors(x, y)) {
    if (result.board[ny][nx] === opponent) {
      const g = getGroup(result.board, nx, ny);
      if (g.liberties.size === 1) score += 180;
    }
  }

  score += Math.min(ownLiberties, 6) * 8;

  // ưu tiên chơi gần quân đã có (kết dính, tạo thế) hơn là rải quân lẻ tẻ
  let neighborStones = 0;
  for (const [nx, ny] of neighbors(x, y)) {
    if (board[ny][nx] !== EMPTY) neighborStones++;
  }
  score += neighborStones * 6;

  // giai đoạn khai cuộc: ưu tiên đường thứ 3 (điểm sao), tránh sát biên
  const edgeDist = Math.min(x, y, SIZE - 1 - x, SIZE - 1 - y);
  if (edgeDist === 2) score += 12;
  if (edgeDist === 0) score -= 10;

  score += Math.random() * 6; // tránh máy chơi y hệt nhau mỗi ván

  return { x, y, score, captured: capturedCount };
}

// AI chọn nước đi trong toàn bộ ô trống hợp lệ (bàn 9x9 chỉ 81 ô nên duyệt hết, không cần rút gọn).
// Nếu người vừa bỏ lượt và không còn nước nào đáng chơi (điểm thấp), máy cũng bỏ lượt để kết thúc ván.
export function getBestMove(board, aiColor, humanColor, { koPoint = null, opponentJustPassed = false } = {}) {
  const candidates = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (board[y][x] !== EMPTY) continue;
      if (koPoint && koPoint.x === x && koPoint.y === y) continue;
      const evaluated = evaluateMove(board, x, y, aiColor, humanColor);
      if (evaluated) candidates.push(evaluated);
    }
  }
  if (candidates.length === 0) return { pass: true };

  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];
  if (opponentJustPassed && best.score < 15) return { pass: true };
  return { x: best.x, y: best.y };
}
